import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth, API, AUTH_REQUEST_TIMEOUT_MS } from "../auth/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeftCircle,
  BadgeCheck,
  CreditCard,
  Loader2,
  Lock,
  Shield,
} from "lucide-react";
import { clearReturnTo, consumeReturnTo, peekReturnTo } from "../lib/returnTo";
import { authRequestErrorMessage } from "../auth/authRequest";
import { authDiagLog } from "../auth/authDiag";
import AuthSessionLoader from "../auth/AuthSessionLoader";
import { LOGIN_HERO_PILL } from "../lib/softLaunch";
import { isInAppBrowser, needsGoogleRedirectLogin } from "../lib/isMobileBrowser";
import {
  GIS_LOAD_MAX_ATTEMPTS,
  GIS_LOAD_TIMEOUT_MESSAGE,
  GIS_POLL_INTERVAL_MS,
  buildGoogleButtonRenderOptions,
  buildGoogleInitOptions,
  computeGoogleButtonWidth,
  getGoogleIdentityService,
} from "../lib/googleSignIn";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

const TRUST_BADGES = [
  {
    icon: BadgeCheck,
    title: "KYC verified",
    body: "Every host and renter passes ID + selfie checks before their first trip.",
  },
  {
    icon: Lock,
    title: "Escrow payments",
    body: "Funds are held securely until your trip completes — no surprise charges.",
  },
  {
    icon: Shield,
    title: "Trip protection",
    body: "Per-trip liability coverage on every booking.",
  },
  {
    icon: CreditCard,
    title: "Stripe secure",
    body: "Card data never touches our servers. PCI-compliant checkout end-to-end.",
  },
];

function TrustBadge({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-[var(--zc-border)] bg-[var(--zc-bg-2)] p-4">
      <div
        className="grid h-9 w-9 shrink-0 place-items-center rounded-xl"
        style={{ background: "var(--zc-accent-soft)", color: "var(--zc-accent)" }}
      >
        <Icon size={16} strokeWidth={2.25} />
      </div>
      <div className="min-w-0">
        <div className="zc-title text-sm">{title}</div>
        <p className="mt-1 text-xs leading-relaxed text-[var(--zc-text-2)]">{body}</p>
      </div>
    </div>
  );
}

export default function Login() {
  const { user, loading, setUser, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const authRequired = !!location.state?.authRequired;
  const pendingReturn = peekReturnTo();
  const buttonRef = useRef(null);
  const buttonWrapRef = useRef(null);
  const gisInitializedRef = useRef(false);
  const handleCredentialRef = useRef(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [gisReady, setGisReady] = useState(false);
  const [gisLoading, setGisLoading] = useState(false);
  const [gisLoadFailed, setGisLoadFailed] = useState(false);
  const [gisRetryKey, setGisRetryKey] = useState(0);
  const loginDiagPrevRef = useRef({ busy, gisReady });
  const navigatedAfterLoginRef = useRef(false);

  const navigateAfterLogin = useCallback(() => {
    if (navigatedAfterLoginRef.current) return;
    navigatedAfterLoginRef.current = true;
    const resume = authRequired || !!peekReturnTo();
    const dest = (resume ? consumeReturnTo() : null) || "/dashboard";
    navigate(dest, { replace: true });
  }, [authRequired, navigate]);

  useEffect(() => {
    const prev = loginDiagPrevRef.current;
    if (prev.busy !== busy || prev.gisReady !== gisReady) {
      authDiagLog("Login", {
        busy,
        gisReady,
        prevBusy: prev.busy,
        prevGisReady: prev.gisReady,
      });
      loginDiagPrevRef.current = { busy, gisReady };
    }
  }, [busy, gisReady]);

  useEffect(() => {
    if (!authRequired && !peekReturnTo()) clearReturnTo();
  }, [authRequired]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const authErr = params.get("auth_error");
    if (authErr) {
      setError(decodeURIComponent(authErr));
      navigate("/login", { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (!loading && user) {
      navigateAfterLogin();
    }
  }, [user, loading, navigateAfterLogin]);

  const handleCredential = useCallback(
    async ({ credential }) => {
      if (!credential) {
        setError("Google sign-in returned no credential.");
        return;
      }
      setBusy(true);
      setError(null);
      try {
        const res = await axios.post(
          `${API}/auth/session`,
          { id_token: credential },
          { withCredentials: true, timeout: AUTH_REQUEST_TIMEOUT_MS },
        );
        if (res.data?.user) {
          setUser(res.data.user);
        } else {
          await checkAuth();
        }
      } catch (e) {
        setError(authRequestErrorMessage(e));
      } finally {
        setBusy(false);
      }
    },
    [checkAuth, setUser],
  );

  handleCredentialRef.current = handleCredential;

  const retryGoogleSignIn = useCallback(() => {
    setGisLoadFailed(false);
    setGisReady(false);
    setGisLoading(true);
    setError(null);
    gisInitializedRef.current = false;
    setGisRetryKey((key) => key + 1);
  }, []);

  useEffect(() => {
    if (loading) return;

    if (!GOOGLE_CLIENT_ID) {
      setGisLoading(false);
      setGisReady(false);
      setGisLoadFailed(false);
      setError("Google sign-in is not configured. REACT_APP_GOOGLE_CLIENT_ID is missing in this build.");
      return;
    }

    let cancelled = false;
    let resizeObserver;
    let pollTimer;
    let loadAttempts = 0;

    setGisLoading(true);
    setGisReady(false);
    setGisLoadFailed(false);

    const failGisLoad = () => {
      if (cancelled) return;
      setGisLoading(false);
      setGisLoadFailed(true);
      setError(GIS_LOAD_TIMEOUT_MESSAGE);
    };

    const renderGoogleButton = () => {
      if (cancelled || !buttonRef.current) return false;
      const gis = getGoogleIdentityService();
      if (!gis) return false;

      const width = computeGoogleButtonWidth(buttonWrapRef.current?.offsetWidth);

      if (!gisInitializedRef.current) {
        const useRedirect = needsGoogleRedirectLogin();
        gis.initialize(
          buildGoogleInitOptions({
            clientId: GOOGLE_CLIENT_ID,
            callback: (response) => handleCredentialRef.current?.(response),
            useRedirect,
            origin: window.location.origin,
          }),
        );
        gisInitializedRef.current = true;
      }

      buttonRef.current.innerHTML = "";
      gis.renderButton(buttonRef.current, buildGoogleButtonRenderOptions(width));
      setGisReady(true);
      setGisLoading(false);
      setGisLoadFailed(false);
      return true;
    };

    const tryRender = () => {
      if (cancelled) return;

      const gis = getGoogleIdentityService();
      const buttonMounted = !!buttonRef.current;

      if (!gis || !buttonMounted) {
        loadAttempts += 1;
        if (loadAttempts >= GIS_LOAD_MAX_ATTEMPTS) {
          failGisLoad();
          return;
        }
        pollTimer = setTimeout(tryRender, GIS_POLL_INTERVAL_MS);
        return;
      }

      if (!renderGoogleButton()) {
        failGisLoad();
        return;
      }

      if (buttonWrapRef.current && typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(() => {
          if (!cancelled && gisInitializedRef.current) renderGoogleButton();
        });
        resizeObserver.observe(buttonWrapRef.current);
      }
    };

    tryRender();

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
      resizeObserver?.disconnect();
      gisInitializedRef.current = false;
    };
  }, [loading, gisRetryKey]);

  if (loading) {
    return <AuthSessionLoader testId="login-page" />;
  }

  return (
    <div className="min-h-[calc(100vh-4rem)]" data-testid="login-page">
      <div className="grid min-h-[inherit] lg:grid-cols-2">
        {/* Hero — mobile-first branding; full-bleed image on lg+ */}
        <section className="relative overflow-hidden lg:min-h-full">
          <div className="absolute inset-0 -z-0">
            <img
              src="https://images.unsplash.com/photo-1583121274602-3e2820c69888?auto=format&fit=crop&w=1600&q=80"
              alt=""
              className="h-full w-full object-cover"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.82) 45%, rgba(253,236,237,0.55) 100%)",
              }}
            />
            <div
              className="absolute inset-0 hidden lg:block"
              style={{
                background:
                  "linear-gradient(90deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.55) 55%, var(--zc-bg) 100%)",
              }}
            />
          </div>

          <div className="relative flex flex-col justify-between px-6 py-8 sm:px-8 sm:py-10 lg:min-h-full lg:px-12 lg:py-16">
            <div className="fade-up max-w-xl">
              <div
                className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/80 px-3 py-1.5 text-[var(--zc-accent)] shadow-sm backdrop-blur-md zc-badge-pill"
              >
                <span className="dot live" />
                {LOGIN_HERO_PILL}
              </div>
              <div className="eyebrow mb-3">Welcome back</div>
              <h1 className="zc-headline text-4xl leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
                Enter <span className="zc-wordmark">Z-Cars</span>.
              </h1>
              <p className="mt-4 max-w-lg text-sm leading-relaxed text-[var(--zc-text-2)] sm:text-base">
                One-tap Google sign-in. Rent a car or list your own — all from one account.
              </p>
            </div>

            <div className="fade-up delay-2 mt-10 hidden gap-3 sm:grid sm:grid-cols-2 lg:mt-16">
              {TRUST_BADGES.map((badge) => (
                <TrustBadge key={badge.title} {...badge} />
              ))}
            </div>
          </div>
        </section>

        {/* Sign-in panel */}
        <section className="flex items-center justify-center bg-[var(--zc-bg)] px-6 py-12 sm:px-8 lg:py-20">
          <div className="fade-up delay-1 w-full max-w-md">
            <div className="zc-card p-6 sm:p-8">
              <div className="eyebrow mb-3">Sign in</div>
              <h2 className="zc-headline text-3xl sm:text-4xl">
                Your fleet is{" "}
                <span className="italic" style={{ color: "var(--zc-accent)" }}>
                  waiting
                </span>
                .
              </h2>
              <p className="mt-3 text-sm text-[var(--zc-text-2)]">
                Continue with Google to access bookings, listings, and your ZCT wallet.
              </p>

              {isInAppBrowser() && (
                <div
                  className="mt-4 rounded-xl border border-[var(--zc-border)] bg-[var(--zc-bg-2)] px-3 py-2.5 text-xs text-[var(--zc-text-2)]"
                  data-testid="login-inapp-hint"
                >
                  Open this page in <strong>Safari</strong> or <strong>Chrome</strong> if Google sign-in
                  fails inside this app&apos;s browser.
                </div>
              )}

              {pendingReturn && (
                <div
                  className="mb-5 mt-6 flex items-center gap-2 rounded-xl px-3 py-2.5 text-xs"
                  style={{ background: "var(--zc-accent-soft)", color: "var(--zc-accent)" }}
                  data-testid="login-return-notice"
                >
                  <ArrowLeftCircle size={14} className="shrink-0" />
                  <span>
                    You&apos;ll be sent back to{" "}
                    <span className="font-mono">
                      {pendingReturn.length > 40
                        ? `${pendingReturn.slice(0, 40)}…`
                        : pendingReturn}
                    </span>{" "}
                    after sign-in.
                  </span>
                </div>
              )}

              <div className="mt-8">
                <div ref={buttonWrapRef} className="flex min-h-[44px] justify-center sm:justify-start">
                  <div
                    ref={buttonRef}
                    className={gisReady ? "" : "hidden"}
                    data-testid="login-google-btn"
                  />
                  {gisLoading && !gisReady && !gisLoadFailed && (
                    <div
                      className="flex h-11 w-full max-w-[320px] items-center justify-center gap-2 rounded-full border border-[var(--zc-border)] bg-[var(--zc-bg-2)] px-4 text-xs text-[var(--zc-text-2)]"
                      data-testid="login-google-loading"
                      role="status"
                      aria-live="polite"
                    >
                      <Loader2 size={14} className="animate-spin text-[var(--zc-accent)]" />
                      Loading Google sign-in…
                    </div>
                  )}
                </div>

                {gisLoadFailed && (
                  <div className="mt-4 flex flex-col items-center gap-3 sm:items-start" data-testid="login-google-retry">
                    <button
                      type="button"
                      onClick={retryGoogleSignIn}
                      className="h-11 w-full max-w-[320px] zc-btn-outline-primary text-sm"
                    >
                      Retry Google sign-in
                    </button>
                  </div>
                )}

                {busy && (
                  <div className="mt-4 flex items-center gap-2 text-xs text-[var(--zc-text-2)]">
                    <Loader2 size={14} className="animate-spin text-[var(--zc-accent)]" />
                    Signing you in…
                  </div>
                )}

                {error && (
                  <div
                    className="mt-4 flex items-start gap-3 rounded-xl border border-[rgba(239,68,68,0.25)] bg-[rgba(239,68,68,0.06)] p-4"
                    data-testid="login-error"
                    role="alert"
                  >
                    <AlertCircle size={18} className="mt-0.5 shrink-0 text-[var(--zc-danger)]" />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[var(--zc-text)]">
                        Couldn&apos;t sign you in
                      </div>
                      <p className="mt-1 text-xs leading-relaxed text-[var(--zc-text-2)]">{error}</p>
                    </div>
                  </div>
                )}

                {!GOOGLE_CLIENT_ID && !error && (
                  <div
                    className="mt-4 rounded-xl border border-dashed border-[var(--zc-border-2)] bg-[var(--zc-bg-3)] p-4 text-center"
                    role="status"
                  >
                    <p className="text-sm text-[var(--zc-text-2)]">
                      Google sign-in is unavailable in this environment.
                    </p>
                    <p className="zc-meta mt-2">
                      Set <span className="font-mono">REACT_APP_GOOGLE_CLIENT_ID</span> to enable One Tap.
                    </p>
                  </div>
                )}
              </div>

              <p className="zc-hint mt-6 text-center sm:text-left">
                By signing in you agree to our terms. Auth handled securely via Google.
              </p>
            </div>

            {/* Trust badges — visible on mobile/tablet where hero hides them */}
            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:hidden">
              {TRUST_BADGES.map((badge) => (
                <TrustBadge key={`mobile-${badge.title}`} {...badge} />
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
